import { Alert } from '@synoptic/ui-kit/alert/alert.tsx';
import { Button } from '@synoptic/ui-kit/button/button.tsx';
import { GroupIcon } from '@synoptic/ui-kit/icons/react/group.tsx';
import { InfoCircleIcon } from '@synoptic/ui-kit/icons/react/info-circle.tsx';
import { LockSquareIcon } from '@synoptic/ui-kit/icons/react/lock-square.tsx';
import { useIsTablet } from '@synoptic/ui-kit/responsive/hooks.ts';
import { FC, useMemo } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { StreamType } from '@/__generated__/graphql.ts';
import {
  streamFormActions,
  streamTypeStepContainer,
} from '@/components/streams/stream-form/stream-form.css.ts';
import { StreamFormInputs } from '@/components/streams/stream-form/stream-form.tsx';
import {
  StreamSelectionItem,
  StreamSelectionTagGroup,
} from '@/components/streams/stream-form/stream-selection-tag-group/stream-selection-tag-group.tsx';

interface CustomStreamTypeStepProps {
  onNext: () => void;
}

export const CustomStreamTypeStep: FC<CustomStreamTypeStepProps> = ({
  onNext,
}) => {
  const { control } = useFormContext<StreamFormInputs>();
  const streamType = useWatch({ control, name: 'streamType' });
  const isTablet = useIsTablet();

  const typeToggleItems: StreamSelectionItem[] = useMemo(
    () => [
      {
        title: 'Private',
        value: StreamType.Private,
        icon: <LockSquareIcon />,
        description:
          'Visible only to you and invited users. Recommended for personal use or team sharing.',
      },
      {
        title: 'Public',
        value: StreamType.Public,
        icon: <GroupIcon />,
        description:
          'Visible to all Synoptic users. Recommended if you want to grow your audience or monetize the stream.',
      },
    ],
    [],
  );

  return (
    <div className={streamTypeStepContainer}>
      <Controller
        name={'streamType'}
        control={control}
        rules={{ required: true }}
        render={({ field }) => (
          <StreamSelectionTagGroup
            items={typeToggleItems}
            onValueChange={field.onChange}
            value={field.value}
            label={'Select the visibility of your stream:'}
          />
        )}
      />

      <Alert icon={<InfoCircleIcon />}>
        Visibility cannot be changed later.
      </Alert>

      <div className={streamFormActions}>
        <Button
          disabled={!streamType}
          type={'button'}
          size={isTablet ? 'large' : 'medium'}
          onClick={onNext}
        >
          Next
        </Button>
      </div>
    </div>
  );
};
